import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  RotateCw,
  Search,
  X,
} from 'lucide-react';
import {
  FoodIcon, BeautyIcon, LeisureIcon, FunIcon, HealthIcon,
  KidsIcon, TechIcon, PharmacyIcon, GroceryIcon, AutoIcon,
} from '../components/CategoryIcons';
import { PageHeader } from '../components/PageHeader';
import { BottomNav } from '../components/BottomNav';
import { SideMenu } from '../components/SideMenu';
import { InstallPrompt } from '../components/InstallPrompt';
import VoucherCard from '../components/VoucherCard';
import NearbyMapHero from '../components/NearbyMapHero';
import ClubPromoBanner from '../components/ClubPromoBanner';
import { voucherApi } from '../api/vouchers';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Cached-location helpers (stale-while-revalidate) ───
// [2026-06-13] The nearby-vouchers row paints from the LAST KNOWN coords
// instantly (no GPS wait), then one silent background fix runs: coords in
// state are replaced only if the user actually moved (> ~2 km), otherwise
// nothing re-renders. Fresh GPS is forced only by the "განახლება" button.
const GEO_LS_KEY = 'pricemap_geo_coords';

function readCachedCoords() {
  try {
    const v = JSON.parse(localStorage.getItem(GEO_LS_KEY));
    if (v && typeof v.lat === 'number' && typeof v.lng === 'number') return v;
  } catch { /* corrupt entry — ignore */ }
  return null;
}

function saveCachedCoords(lat, lng) {
  try {
    localStorage.setItem(GEO_LS_KEY, JSON.stringify({ lat, lng, ts: Date.now() }));
  } catch { /* private mode — best-effort */ }
}

function distanceKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Voucher categories — single source of truth on the frontend.
// Must stay in sync with backend voucher_system/models.py CATEGORIES_KA.
// Order here = display order on the home category row.
// Icons are line illustrations (CategoryIcons.jsx) on a neutral
// slate-100 circle; the per-category accent shows only on the active one.

// Home page: how many cards each block shows.
const NEARBY_ROW = 6;
const TRENDING_ROW = 8;
const ALL_MAX = 20;
const VOUCHER_CATEGORIES = [
  { id: 'K', label: 'კვება',         Icon: FoodIcon,     text: 'text-orange-600'  },
  { id: 'S', label: 'სილამაზე',      Icon: BeautyIcon,   text: 'text-pink-600'    },
  { id: 'D', label: 'დასვენება',     Icon: LeisureIcon,  text: 'text-amber-600'   },
  { id: 'G', label: 'გართობა',       Icon: FunIcon,      text: 'text-violet-600'  },
  { id: 'J', label: 'ჯანმრთელობა',   Icon: HealthIcon,   text: 'text-sky-600'     },
  { id: 'B', label: 'ბავშვები',      Icon: KidsIcon,     text: 'text-emerald-600' },
  { id: 'T', label: 'ტექნიკა',       Icon: TechIcon,     text: 'text-slate-600'   },
  { id: 'A', label: 'აფთიაქი',       Icon: PharmacyIcon, text: 'text-teal-600'    },
  { id: 'M', label: 'სუპერმარკეტი',  Icon: GroceryIcon,  text: 'text-green-700'   },
  { id: 'V', label: 'ავტო',          Icon: AutoIcon,     text: 'text-red-600'     },
];

export function HomePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [q, setQ] = useState('');
  // [Sprint UI-6] Everything on the home page filters in place: the
  // header search (applied on submit) and the category row narrow every
  // block below; nothing navigates away except opening a voucher.
  const [query, setQuery] = useState('');
  // ?cat=K pre-selects a category (club redirects, shared links).
  const [activeCat, setActiveCat] = useState(() => {
    const c = params.get('cat');
    return VOUCHER_CATEGORIES.some((x) => x.id === c) ? c : null;
  });
  const [showAll, setShowAll] = useState(false);
  const matches = useCallback((o) => {
    if (activeCat && o.partner_category !== activeCat) return false;
    if (!query) return true;
    const needle = query.toLowerCase();
    return [o.title, o.partner_name, o.description].some((v) => (v || '').toLowerCase().includes(needle));
  }, [activeCat, query]);
  const filtering = !!(activeCat || query);
  const [geoStatus, setGeoStatus] = useState('pending');
  const [coords, setCoords] = useState(null);
  const [nearbyVouchers, setNearbyVouchers] = useState(null);
  // Partners picked on the map hero. Their vouchers move to the front of
  // the nearby row (the row is reordered, not duplicated).
  const [pickedPartners, setPickedPartners] = useState(null);
  const handleSelectPartners = useCallback((ids) => setPickedPartners(ids), []);
  const nearbyRowOffers = useMemo(() => {
    if (!nearbyVouchers) return null;
    const base = nearbyVouchers.filter(matches);
    if (!pickedPartners?.length) return base.slice(0, NEARBY_ROW);
    const picked = new Set(pickedPartners);
    const first = base.filter((o) => picked.has(o.partner_id));
    const rest = base.filter((o) => !picked.has(o.partner_id));
    return [...first, ...rest].slice(0, Math.max(NEARBY_ROW, first.length));
  }, [nearbyVouchers, pickedPartners, matches]);
  const [nearbyTick, setNearbyTick] = useState(0);
  const [nearbyRefreshing, setNearbyRefreshing] = useState(false);

  // [Sprint UI-6] The trending row and the "all vouchers" list do not
  // need a location. They use the nearby list when there is one (same
  // offers, distance-sorted) and this plain fetch otherwise, so a user
  // who declined location still sees the whole catalogue.
  const [plainOffers, setPlainOffers] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await voucherApi.getOffers({});
        if (!cancelled) setPlainOffers(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setPlainOffers([]); }
    })();
    return () => { cancelled = true; };
  }, []);
  const allOffers = nearbyVouchers ?? plainOffers;
  const trendingOffers = useMemo(() => {
    if (!allOffers) return null;
    return allOffers
      .filter(matches)
      .sort((a, b) => (b.voucher_count || 0) - (a.voucher_count || 0))
      .slice(0, TRENDING_ROW);
  }, [allOffers, matches]);
  const filteredAll = useMemo(() => (allOffers ? allOffers.filter(matches) : null), [allOffers, matches]);
  const listOffers = useMemo(
    () => (filteredAll ? (showAll ? filteredAll : filteredAll.slice(0, ALL_MAX)) : null),
    [filteredAll, showAll],
  );

  // Header search filters the vouchers on this page (applied on submit).
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setQuery(q.trim());
    setShowAll(false);
  };
  const clearSearch = () => { setQ(''); setQuery(''); };
  const handleCategoryPick = (id) => {
    setActiveCat((cur) => (cur === id ? null : id));
    setShowAll(false);
  };

  const handleLogoClick = () => {
    navigate('/', { replace: true });
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!navigator.geolocation) { setGeoStatus('denied'); return; }
    let cancelled = false;

    // Fast path: last known coords on disk → show the row immediately,
    // then revalidate position once in the background. A failed silent
    // fix is simply ignored — valid content is already on screen.
    const cachedCoords = readCachedCoords();
    if (cachedCoords) {
      setCoords({ lat: cachedCoords.lat, lng: cachedCoords.lng });
      setGeoStatus('granted');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          const fresh = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          saveCachedCoords(fresh.lat, fresh.lng);
          if (distanceKm(fresh, cachedCoords) > 2) setCoords(fresh);
        },
        () => { /* silent — stale coords stay on screen */ },
        { timeout: 15000, maximumAge: 5 * 60 * 1000 },
      );
      return () => { cancelled = true; };
    }

    // [2026-06-13] GPS cold-fix on mobile often exceeds a short timeout.
    // Previously timeout=5000 and ANY error flipped status to 'denied' —
    // so after a refresh the row vanished even with permission granted,
    // and the user had to tap repeatedly until GPS warmed up. Now:
    //   • first try 10s; on timeout/unavailable (code 2/3) retry once
    //     with a 20s window, accepting a cached fix up to 30 min old;
    //   • 'denied' is reserved for a real permission denial (code 1).
    const performRequest = (attempt = 0) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          saveCachedCoords(pos.coords.latitude, pos.coords.longitude);
          setGeoStatus('granted');
          localStorage.setItem('pricemap_geo_decision', 'granted');
        },
        (err) => {
          if (cancelled) return;
          if (err.code === 1) {
            setGeoStatus('denied');
            localStorage.setItem('pricemap_geo_decision', 'denied');
            return;
          }
          if (attempt === 0) { performRequest(1); return; }
          setGeoStatus('denied');
        },
        attempt === 0
          ? { timeout: 10000, maximumAge: 5 * 60 * 1000 }
          : { timeout: 20000, maximumAge: 30 * 60 * 1000 },
      );
    };
    const cachedDecision = localStorage.getItem('pricemap_geo_decision');
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => {
          if (cancelled) return;
          if (result.state === 'granted') performRequest();
          else if (result.state === 'denied') {
            setGeoStatus('denied');
            localStorage.setItem('pricemap_geo_decision', 'denied');
          } else {
            if (cachedDecision === 'denied') setGeoStatus('denied');
            else performRequest();
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (cachedDecision === 'denied') setGeoStatus('denied');
          else performRequest();
        });
    } else {
      if (cachedDecision === 'denied') setGeoStatus('denied');
      else performRequest();
    }
    return () => { cancelled = true; };
  }, []);

  const handleEnableLocation = async () => {
    if (typeof window === 'undefined' || !navigator.geolocation) return;

    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

    const showBlockedToast = () => toast.error(
      isIOS
        ? 'ლოკაცია დაბლოკილია. iPhone-ზე: პარამეტრები → Safari → ლოკაცია → ნება დართე'
        : 'ბრაუზერში ლოკაცია დაბლოკილია. მისამართის ზოლში ჩაკეტილ ღილაკს დააჭირე და მიეცი წვდომა',
      { duration: 8000 }
    );

    const requestOnce = (attempt = 0) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          saveCachedCoords(pos.coords.latitude, pos.coords.longitude);
          setGeoStatus('granted');
          localStorage.setItem('pricemap_geo_decision', 'granted');
        },
        (err) => {
          if (err.code === 1) {
            setGeoStatus('denied');
            showBlockedToast();
            localStorage.setItem('pricemap_geo_decision', 'denied');
            return;
          }
          if (attempt === 0) { requestOnce(1); return; }
          setGeoStatus('denied');
          toast.error('ლოკაციის მიღება ვერ მოხერხდა — სცადე კიდევ ერთხელ');
        },
        attempt === 0
          ? { timeout: 10000, maximumAge: 0 }
          : { timeout: 20000, maximumAge: 30 * 60 * 1000 },
      );
    };

    if (isIOS || !navigator.permissions?.query) {
      localStorage.removeItem('pricemap_geo_decision');
      setGeoStatus('pending');
      requestOnce();
      return;
    }

    if (navigator.permissions?.query) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        if (result.state === 'denied') { showBlockedToast(); return; }
      } catch {}
    }
    localStorage.removeItem('pricemap_geo_decision');
    setGeoStatus('pending');
    requestOnce();
  };

  useEffect(() => {
    if (geoStatus !== 'granted' || !coords) return;
    let cancelled = false;
    (async () => {
      try {
        const opts = { lat: coords.lat, lng: coords.lng };
        const data = await voucherApi.getOffers(opts);
        // Full list: the map hero counts and pins all of them; the row
        // below still shows the first 6.
        if (!cancelled) setNearbyVouchers(Array.isArray(data) ? data : []);
      } catch (e) { if (!cancelled) setNearbyVouchers([]); }
    })();
    return () => { cancelled = true; };
  }, [coords, geoStatus, nearbyTick]);

  const refreshNearby = () => {
    if (nearbyRefreshing || geoStatus !== 'granted') return;
    setNearbyRefreshing(true);
    voucherApi.clearOffersCache();
    // Forced FRESH fix — this button is the explicit "re-locate me" action.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const fresh = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        saveCachedCoords(fresh.lat, fresh.lng);
        setCoords(fresh);
        setNearbyTick((t) => t + 1);
        setNearbyRefreshing(false);
      },
      () => {
        // GPS failed — refetch with the coords we have, but say so.
        toast.error('ლოკაციის განახლება ვერ მოხერხდა — ნაჩვენებია ბოლო ცნობილი მდებარეობით');
        setNearbyTick((t) => t + 1);
        setNearbyRefreshing(false);
      },
      { timeout: 10000, maximumAge: 0 },
    );
  };

  const handleVoucherClick = (offer) => {
    navigate(`/voucher/${offer._id}`, { state: { offer } });
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="home-page">
      <div className="contents md:hidden">
        <PageHeader
          variant="home-logo"
          onLogoClick={handleLogoClick}
          onFavClick={() => navigate('/favorites')}
          onMenuClick={() => setMenuOpen(true)}
        />
      </div>
      <main className="px-5 pt-0 pb-[88px] md:max-w-6xl md:mx-auto md:px-8 lg:px-12 md:pt-6 md:pb-12">
        {/* [Sprint UI-6] Deal-site page order: search, short map, category
            row, nearby lane, trending lane, the catalogue, footer. The club
            row is gone for now — it comes back later as a dismissable
            banner. */}
        <form onSubmit={handleSearchSubmit} className="pb-3" data-testid="home-search">
          <div className="flex items-center gap-2 h-12 pl-[18px] pr-1.5 rounded-full bg-white border-2 border-blue-800 focus-within:ring-[3px] focus-within:ring-blue-800/15">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="მოძებნე ფასდაკლება: მასაჟი, სტომატოლოგია…"
              className="flex-1 min-w-0 bg-transparent outline-none text-[14.5px] text-slate-800 placeholder:text-slate-400"
              data-testid="home-search-input"
              autoComplete="off"
            />
            {(q || query) && (
              <button
                type="button"
                onClick={clearSearch}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0"
                aria-label="გასუფთავება"
                data-testid="home-search-clear"
              >
                <X className="w-3.5 h-3.5 text-slate-500" strokeWidth={2} />
              </button>
            )}
            <button
              type="submit"
              className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center shrink-0 active:scale-95 transition-transform"
              aria-label="ძებნა"
            >
              <Search className="w-[17px] h-[17px] text-white" strokeWidth={2.2} />
            </button>
          </div>
        </form>

        <NearbyMapHero
          geoStatus={geoStatus}
          coords={coords}
          offers={nearbyVouchers}
          onSelectPartners={handleSelectPartners}
          onEnableLocation={handleEnableLocation}
        />

        <CategoryRow active={activeCat} onPick={handleCategoryPick} />

        {geoStatus !== 'denied' && (
          <VoucherRow
            title="ფასდაკლებები შენ მიმდებარედ"
            offers={nearbyRowOffers}
            highlightPartners={pickedPartners}
            onCardClick={handleVoucherClick}
            onRefresh={refreshNearby}
            refreshing={nearbyRefreshing}
            emptyText={filtering ? 'მალე დაემატება' : null}
            testId="nearby-vouchers"
          />
        )}
        <VoucherRow
          title="ტრენდული შეთავაზებები"
          subtitle="ყველაზე ხშირად გამოყენებული ვაუჩერები"
          topClass="mt-9"
          offers={trendingOffers}
          onCardClick={handleVoucherClick}
          emptyText={filtering ? 'მალე დაემატება' : null}
          testId="trending-vouchers"
        />
        <AllVouchers
          offers={listOffers}
          total={filteredAll ? filteredAll.length : 0}
          onCardClick={handleVoucherClick}
          onShowMore={() => setShowAll(true)}
          emptyText={filtering ? 'მალე დაემატება' : null}
        />
      </main>
      <ClubPromoBanner />
      <BottomNav />
      <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <InstallPrompt />
    </div>
  );
}

/* ─── Sub-components ─── */

function VoucherRow({ title, subtitle, offers, onCardClick, onSeeAll, onRefresh, refreshing, testId, titleClassName = 'text-slate-900', filterSlot, highlightPartners, infoText, emptyText, topClass = 'mt-6' }) {
  const loading = offers === null;
  const [infoOpen, setInfoOpen] = useState(false);
  const laneRef = useRef(null);
  const highlighted = new Set(highlightPartners || []);
  // A new pick moves its cards to the front — scroll the lane back to
  // the start so they are the ones on screen.
  const pickKey = (highlightPartners || []).join(',');
  useEffect(() => {
    if (pickKey) laneRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
  }, [pickKey]);
  const isEmpty = !loading && offers.length === 0;
  // An empty row disappears — unless a filter is on, when "მალე
  // დაემატება" is the honest answer for that category or search.
  if (isEmpty && !emptyText) return null;

  // Desktop caps the row at 4 cards so it stays one line and the next
  // section doesn't get pushed off-screen by a tall multi-row grid. Mobile keeps the full list reachable via
  // horizontal scroll. The remaining offers are surfaced via the
  // "ყველას ნახვა" link in the header.
  const desktopOffers = loading ? null : offers.slice(0, 4);

  return (
    <section className={topClass} data-testid={testId}>
      <div className="relative flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
          <h2 className={`text-[16px] font-extrabold tracking-tight whitespace-nowrap ${titleClassName}`}>{title}</h2>
          {subtitle && <div className="w-full text-[13px] text-slate-500 -mt-0.5">{subtitle}</div>}
          {infoText && (
            <button
              type="button"
              onClick={() => setInfoOpen((v) => !v)}
              className={`w-5 h-5 rounded-full text-[11px] font-bold leading-none flex items-center justify-center shrink-0 transition-colors ${
                infoOpen ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
              aria-label="განმარტება"
              aria-expanded={infoOpen}
              data-testid={`${testId}-info`}
            >
              ?
            </button>
          )}
        </div>
        {infoOpen && (
          <>
            {/* Backdrop — tap anywhere to close */}
            <div className="fixed inset-0 z-30" onClick={() => setInfoOpen(false)} aria-hidden="true" />
            <div
              className="absolute left-0 top-full z-40 mt-2 max-w-[300px] bg-white rounded-2xl shadow-xl ring-1 ring-slate-900/5 px-4 py-3 text-[13px] leading-snug text-slate-700"
              role="note"
              data-testid={`${testId}-info-text`}
            >
              {infoText}
            </div>
          </>
        )}
        <div className="flex items-center gap-3">
          {filterSlot}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing || loading}
              className="w-9 h-9 -my-1 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 active:scale-95 transition-all disabled:opacity-50 shrink-0"
              aria-label={refreshing ? 'განახლება...' : 'განახლება'}
              title="განახლება"
              data-testid={`${testId}-refresh`}
            >
              <RotateCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={2.2} />
            </button>
          )}
          {!loading && onSeeAll && (
            <button
              onClick={onSeeAll}
              className="hidden md:inline-block text-[13px] text-blue-600 font-medium hover:underline"
              data-testid={`${testId}-see-all`}
            >
              ყველას ნახვა
            </button>
          )}
        </div>
      </div>

      {isEmpty && (
        <p className="text-[13.5px] text-slate-500 py-2" data-testid={`${testId}-empty`}>{emptyText}</p>
      )}

      {/* Mobile: horizontal carousel — full list reachable via swipe */}
      {/* Vertical padding gives the highlight ring room: overflow-x on the
          lane also clips vertically, so without it the ring is cut off. */}
      {!isEmpty && <div ref={laneRef} className="md:hidden overflow-x-auto scrollbar-hide -mx-5 -my-1.5">
        <div className="flex gap-3 px-5 py-1.5 w-max">
          {loading
            ? [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-[283px] shrink-0 rounded-2xl bg-slate-100 animate-pulse"
                  style={{ height: 320 }}
                />
              ))
            : offers.map((offer) => (
                <div key={offer._id} className={`w-[283px] shrink-0 rounded-2xl ${highlighted.has(offer.partner_id) ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[#fafafa]' : ''}`}>
                  <VoucherCard offer={offer} onClick={onCardClick} />
                </div>
              ))}
          {/* "See all" tail card — appears after the last voucher once the
              user scrolls the mobile lane to the end. Fully transparent
              (no background, no circle): only the arrow + label show.
              Mobile only; desktop uses the header link + grid. */}
          {!loading && onSeeAll && offers.length > 0 && (
            <button
              onClick={onSeeAll}
              className="w-[96px] shrink-0 flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform"
              data-testid={`${testId}-see-all-tail`}
              aria-label="ყველას ნახვა"
            >
              <ChevronRight className="w-7 h-7 text-blue-600" strokeWidth={2} />
              <span className="text-[12px] font-medium text-blue-600 text-center leading-tight">
                ყველას ნახვა
              </span>
            </button>
          )}
        </div>
      </div>}

      {/* Desktop: single row, up to 4 cards. */}
      {!isEmpty && <div className="hidden md:grid md:grid-cols-4 md:gap-4">
        {loading
          ? [1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-100 animate-pulse"
                style={{ height: 320 }}
              />
            ))
          : desktopOffers.map((offer) => (
              <div key={offer._id} className={`rounded-2xl ${highlighted.has(offer.partner_id) ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-[#fafafa]' : ''}`}>
                <VoucherCard offer={offer} onClick={onCardClick} />
              </div>
            ))}
      </div>}
    </section>
  );
}

/* CategoryRow — the ten voucher categories as round icons, one swipe
   wide on phones. Sizes follow the deal-site row measured on
   groupon.com mobile (2026-09-16): 56px circle, 20px icon, bold label
   under it. Georgian category names are single long words, so the item
   is 92px wide (not 56) and the label never wraps. A tap filters the page in place;
   the active category tints its circle and label, tapping it again
   clears the filter. */
function CategoryRow({ active, onPick }) {
  return (
    <div className="overflow-x-auto scrollbar-hide -mx-5 mt-4 py-3.5 bg-blue-50 md:mx-0 md:rounded-2xl" data-testid="category-row">
      <div className="flex gap-2.5 px-4 w-max md:px-0 md:w-auto md:justify-between">
        {VOUCHER_CATEGORIES.map(({ id, label, Icon, text }) => {
          const on = active === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onPick(id)}
              className="w-[92px] shrink-0 flex flex-col items-center gap-0.5 active:scale-95 transition-transform"
              aria-pressed={on}
              data-testid={`category-${id}`}
            >
              {/* White circle with a thin grey edge (reads on the blue band
                  on phones), coloured stroke and label; the active one gets
                  a ring in its colour. */}
              <span className={`w-12 h-12 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center transition-shadow ${text} ${on ? 'ring-2 ring-current' : ''}`}>
                <Icon className="w-7 h-7" strokeWidth={1.25} />
              </span>
              <span className={`text-[11.5px] font-bold leading-4 text-center whitespace-nowrap ${text}`}>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* AllVouchers — the catalogue itself: full-width cards in a column on
   phones, a grid on desktop, capped at ALL_MAX with a link to the rest. */
function AllVouchers({ offers, total, onCardClick, onShowMore, emptyText }) {
  const loading = offers === null;
  const isEmpty = !loading && offers.length === 0;
  if (isEmpty && !emptyText) return null;
  const hidden = loading ? 0 : total - offers.length;
  return (
    <section className="mt-6" data-testid="all-vouchers">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[16px] font-extrabold tracking-tight text-slate-900">ყველა ვაუჩერი</h2>
        {!loading && !isEmpty && <span className="text-[13px] text-slate-500 tabular-nums">{total}</span>}
      </div>
      {isEmpty ? (
        <p className="text-[13.5px] text-slate-500 py-2" data-testid="all-vouchers-empty">{emptyText}</p>
      ) : (
        <div className="flex flex-col gap-6 md:grid md:grid-cols-4 md:gap-4">
          {loading
            ? [1, 2, 3].map((i) => <div key={i} className="rounded-2xl bg-slate-100 animate-pulse" style={{ height: 320 }} />)
            : offers.map((offer) => <VoucherCard key={offer._id} offer={offer} onClick={onCardClick} />)}
        </div>
      )}
      {hidden > 0 && (
        <button
          onClick={onShowMore}
          className="mt-6 w-full h-12 rounded-full border border-slate-300 bg-white text-[14px] font-semibold text-slate-800 active:bg-slate-50 md:w-auto md:px-8"
          data-testid="all-vouchers-more"
        >
          კიდევ {hidden} ვაუჩერი
        </button>
      )}
    </section>
  );
}

export default HomePage;
